import {CHANNEL_INFO_BY_CHANNEL, CHANNEL_TYPE_PAID_MEDIA, SHEET_CHANNEL} from '../../channels/channels';

/**
 * Qué cuentas ofrece el modal «Vincular una cuenta» y en qué orden (card jaM5imSj).
 *
 * `get-accounts/<canal>` sólo trae nombre e id. La actividad la calcula el backend en
 * `agency/global-lines/candidates/` (`GlobalLineCandidatesService`), el mismo clasificador que usa el
 * túnel de alta: acá se cruza por `account_id` y NO se reimplementa, para que una cuenta no diga
 * «activa» en el alta y «sin campañas» en el Panel sobre los mismos datos.
 */

export const ACTIVITY_ACTIVE = 'ACTIVE';
export const ACTIVITY_DORMANT = 'DORMANT';
export const LIVENESS_DEAD = 'DEAD';

// Primero lo que se mueve; al final lo que la plataforma dio por cerrado. UNKNOWN (sin catálogo
// sincronizado todavía, típico de una cuenta recién conectada) va en el medio: no se sabe.
const RANK_ACTIVE = 0;
const RANK_UNKNOWN = 1;
const RANK_DORMANT = 2;
const RANK_DEAD = 3;

/** ¿Tiene sentido pedir la actividad de este canal? El endpoint sólo conoce paid media. */
export function channelHasActivity(channel) {
    const info = CHANNEL_INFO_BY_CHANNEL[channel];
    return !!info && info.channelType === CHANNEL_TYPE_PAID_MEDIA && channel !== SHEET_CHANNEL;
}

/** `{'<account_id>': candidato}` a partir de la respuesta del endpoint. Tolera cualquier forma. */
export function candidatesByAccountId(response) {
    const accounts = (response && response.status === 'OK' && response.data && response.data.accounts) || [];
    const map = {};
    accounts.forEach((candidate) => {
        if (candidate && candidate.account_id !== undefined && candidate.account_id !== null) {
            map[String(candidate.account_id)] = candidate;
        }
    });
    return map;
}

function rankOf(candidate) {
    if (!candidate) return RANK_UNKNOWN;
    if (candidate.liveness === LIVENESS_DEAD) return RANK_DEAD;
    if (candidate.activity === ACTIVITY_ACTIVE) return RANK_ACTIVE;
    if (candidate.activity === ACTIVITY_DORMANT) return RANK_DORMANT;
    return RANK_UNKNOWN;
}

function linkedClientIdOf(candidate) {
    return candidate && candidate.agency_client ? String(candidate.agency_client.id) : null;
}

/**
 * Las cuentas a ofrecer: sin las que este cliente ya tiene, ordenadas por actividad y, dentro de
 * cada grupo, primero las libres y después las que ya son de otro cliente — así lo primero que se
 * ve es elegible (QA-cuentas-03). Después, el orden del backend (alfabético): `sort` es estable.
 *
 * «Las que este cliente ya tiene» salen de dos fuentes, porque cada una sola deja huecos:
 * - `linkedAccountIds`, lo que el Panel dibuja. Viene del listado de cuentas del cliente, que el
 *   backend filtra por los clientes ASIGNADOS a quien mira: en un cliente que el usuario abre sin
 *   tenerlo asignado llega vacío y el modal ofrecía cuentas que el cliente ya tenía (QA-cuentas-05).
 * - La actividad (`agency_client`), que no filtra por usuario, pero trae un solo cliente por cuenta
 *   (el de menor id) cuando la cuenta está en varios.
 */
export function orderAccounts(accounts, candidates, linkedAccountIds, clientId) {
    const linked = new Set((linkedAccountIds || []).map(String));
    const byId = candidates || {};
    const thisClient = clientId === undefined || clientId === null ? null : String(clientId);
    const takenByOther = (candidate) => {
        const owner = linkedClientIdOf(candidate);
        return owner !== null && owner !== thisClient ? 1 : 0;
    };
    return (accounts || [])
        .filter((account) => !linked.has(String(account.id)))
        .filter((account) => thisClient === null || linkedClientIdOf(byId[String(account.id)]) !== thisClient)
        .map((account, index) => {
            const candidate = byId[String(account.id)];
            return {account, index, rank: rankOf(candidate), taken: takenByOther(candidate)};
        })
        .sort((a, b) => (a.rank - b.rank) || (a.taken - b.taken) || (a.index - b.index))
        .map((entry) => entry.account);
}

/**
 * Las claves i18n de las pistas de una cuenta. La otra vinculación se dice SIN nombrar al cliente:
 * el endpoint no filtra por los clientes asignados a quien pregunta, y un usuario no admin no tiene
 * por qué leer en pantalla el nombre de un cliente que no ve.
 *
 * OJO: esto es sólo de UI, no aislamiento. `agency/global-lines/candidates/` manda
 * `agency_client.name` igual al navegador (dentro de la misma agencia, nunca de otra). Scopearlo es
 * deuda del backend (`GlobalLineCandidatesService`), compartida con el túnel de alta.
 */
export function hintKeysOf(candidate, clientId) {
    if (!candidate) return [];
    const keys = [];
    const rank = rankOf(candidate);
    if (rank === RANK_DEAD) keys.push('control_central.link_account_hint_dead');
    else if (rank === RANK_ACTIVE) keys.push('control_central.link_account_hint_active');
    else if (rank === RANK_DORMANT) keys.push('control_central.link_account_hint_dormant');

    const other = candidate.agency_client;
    if (other && String(other.id) !== String(clientId)) {
        keys.push('control_central.link_account_hint_other_client');
    }
    return keys;
}
